import React, { useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import { getCurrencySymbol } from "../../helper/formatCurrency";
import { useRestaurantConfig } from "../../utils/restaurantConfig";
import { PriceUpdateHeader, MobileCardView, PriceTable } from "./components";
import { usePriceUpdate } from "./hooks";
import {
  exportChanges,
  exportAllPrices,
  transformProductsToRows,
  paginateData,
  MOBILE_BREAKPOINT,
  ROWS_PER_PAGE,
} from "./utils";

/**
 * Price Update Page
 * Allows bulk updating of product prices and offer prices
 * Supports both mobile card view and desktop table view
 */
function PriceUpdate() {
  const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
  const [anchorEl, setAnchorEl] = useState(null);

  const {
    products,
    editedPrices,
    searchTerm,
    showRecentlyUpdatedOnly,
    isCompactView,
    page,
    hasMore,
    priceErrors,
    setSearchTerm,
    setShowRecentlyUpdatedOnly,
    setIsCompactView,
    setPage,
    handleNewPriceChange,
    handleRemoveOfferPrice,
    handleSubmit,
    fetchAllProductsForExport,
    getFilteredProducts,
    handleToggleVisibility,
  } = usePriceUpdate();

  const { locale } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);

  const filteredProducts = getFilteredProducts();
  const productRows = transformProductsToRows(filteredProducts, editedPrices);
  const displayData = paginateData(productRows, page, ROWS_PER_PAGE);
  const totalPages = Math.ceil(productRows.length / ROWS_PER_PAGE);

  // Export menu handlers
  const handleExportClick = (event) => setAnchorEl(event.currentTarget);
  const handleExportClose = () => setAnchorEl(null);

  const handleExportChanges = () => {
    exportChanges(editedPrices, products);
  };

  const handleExportAll = async () => {
    const allProducts = await fetchAllProductsForExport();
    exportAllPrices(allProducts);
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-gray-50 via-white to-gray-50 px-2 py-3 overflow-hidden">
      {/* Header Section */}
      <div className="flex-shrink-0">
        <PriceUpdateHeader
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          isCompactView={isCompactView}
          onToggleView={() => setIsCompactView(!isCompactView)}
          onExportClick={handleExportClick}
          onSubmit={handleSubmit}
          hasChanges={Object.keys(editedPrices).length > 0}
          hasErrors={Object.keys(priceErrors).length > 0}
          showRecentlyUpdatedOnly={showRecentlyUpdatedOnly}
          onRecentlyUpdatedChange={(e) =>
            setShowRecentlyUpdatedOnly(e.target.checked)
          }
          anchorEl={anchorEl}
          onExportClose={handleExportClose}
          onExportChanges={handleExportChanges}
          onExportAll={handleExportAll}
        />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-hidden">
        {isMobile ? (
          <MobileCardView
            productRows={displayData}
            isCompactView={isCompactView}
            handleNewPriceChange={handleNewPriceChange}
            handleRemoveOfferPrice={handleRemoveOfferPrice}
            handleToggleVisibility={handleToggleVisibility}
            priceErrors={priceErrors}
            currencySymbol={currencySymbol}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : (
          <PriceTable
            productRows={displayData}
            isCompactView={isCompactView}
            handleNewPriceChange={handleNewPriceChange}
            handleRemoveOfferPrice={handleRemoveOfferPrice}
            handleToggleVisibility={handleToggleVisibility}
            priceErrors={priceErrors}
            currencySymbol={currencySymbol}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}

export default PriceUpdate;
